<template>
  <div class="message-row" :class="[{ system: isSystem, user: isUser, pending: isPending }, message.display.type]">
    <div class="message" @mouseover="hoverStart" @mouseleave="hoverEnd"
      :class="{ 'show-actions': showActions || actionOccurred || downloadMenuOpen }">
      <div class="main-section">
        <div class="header">
          <div class="author" v-if="isUser">
            <!-- <user-avatar class="user-avatar" :name="message.author.displayName"></user-avatar> -->
          </div>
          <div class="author" v-else>
            <img src="@/assets/cb-logo.png" height="50px">
          </div>
        </div>
        <div class="content" :class="[{ 'is-error': message.error }, `is-${message.display.type}`]">
          <div v-if="inProgress">
            <div v-if="isSystem && latest" class="status">
              <strong>{{ statusLabel(latest.status) }}{{ statusDots }}</strong>
            </div>
          </div>
          <div v-else>
            <p v-html="md.render(message.text.replace(/```markdown\s*\n?/, '').replace(/\n?```\s*$/, ''))"></p>
            <div style="display: none;">{{ message.id }}</div>
            <component :is="componentName" ref="messageTypeComponent" :message="message"
              :key="`table-${message.id}`" v-bind="chartMessageExtraProps"
              v-if="componentName !== 'ChatMessageText'"></component>
            <!-- Reasoning / debug popout: reasoning always; debug sections only on localhost -->
            <div class="workings" v-if="workingsVisible">
              <div class="workings-header">
                <h3>Reasoning</h3>
                <a href="#" class="workings-close" @click.prevent="workingsVisible = false"
                  @keyup.esc="workingsVisible = false" tabindex="0" title="Close">
                  <font-awesome-icon icon="xmark" />
                </a>
              </div>
              <div class="workings-body">
                <div class="workings-reasoning" v-if="reasoning" v-html="md.render(reasoning)"></div>
                <p v-else class="workings-empty">No reasoning recorded.</p>

                <template v-if="isLocalhost">
                  <template v-if="message.toolHistory && message.toolHistory.length">
                    <hr>
                    <h4>Parameters used</h4>
                    <ol>
                      <li class="working" v-for="(entry, index) in message.toolHistory"
                        :key="`message-${message.id}-tool-${index}`">
                        <strong>{{ toolName(entry.tool) }}</strong>
                        <pre class="tool-params" v-if="entry.params">{{ formatParams(entry.params) }}</pre>
                      </li>
                    </ol>
                  </template>
                  <template v-if="filteredStatusHistory.length">
                    <hr>
                    <h4>Status</h4>
                    <ol>
                      <li class="working" v-for="(status, index) in filteredStatusHistory"
                        :key="`message-${message.id}-status-${index}`">
                        <strong>{{ statusLabel(status.status) }}</strong>
                        <p class="description" v-if="status.params && status.params.workings"
                          v-html="md.render(status.params.workings)"></p>
                      </li>
                    </ol>
                  </template>
                  <div class="workings-debug-actions">
                    <button type="button" class="workings-debug-btn" @click="showDebug"
                      title="Show full debug data">Show full
                      debug</button>
                  </div>
                </template>
              </div>
            </div>

            <!-- Full debug modal (localhost only) -->
            <div v-if="debugVisible && isLocalhost" class="modal" ref="modal">
              <div class="modal-background" @click="hideDebug"></div>
              <div class="modal-content">
                <div class="modal-actions">
                  <button type="button" class="modal-close" @click="hideDebug" @keyup.esc="hideDebug" tabindex="0"
                    title="Close">
                    <font-awesome-icon icon="xmark" />
                  </button>
                </div>
                <h2>Response Message</h2>
                <p>{{ message.text }}</p>
                <h2>History</h2>
                <pre>{{ JSON.stringify(message.data && message.data.history, null, 2) }}</pre>
                <h2>Stores</h2>
                <pre>{{ JSON.stringify(message.data && message.data.stores, null, 2) }}</pre>
                <h2>Briefing</h2>
                <pre>{{ message.data && message.data.briefing }}</pre>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="action-container">
        <div>{{ formatEventDateSimple(message.createdAt) }}</div>
        <template v-if="isChartMessage && !inProgress && chatChartOptionsEnabled">
          <v-select v-if="chartTypeSelectItems.length > 1" :items="chartTypeSelectItems" v-model="chartDisplaySubType"
            item-text="text" item-value="value" label="Chart type" solo flat hide-details dense
            class="action-chart-type-select"></v-select>
          <div class="action" @click="downloadChartImage" title="Download chart as PNG">
            <font-awesome-icon icon="file-image" />
          </div>
        </template>
        <div class="action" v-if="hasCopyClipboard" @click="copyToClipboard(message)"
          :class="{ 'actioned': copyActioned }" title="Copy to clipboard">
          <font-awesome-icon icon="copy" />
        </div>
        <v-menu v-if="hasDownload" offset-y left v-model="downloadMenuOpen">
          <div slot="activator" class="action" :class="{ 'actioned': downloadActioned }"
            title="Download CSV or Excel">
            <font-awesome-icon icon="download" />
          </div>
          <v-list dense>
            <v-list-tile @click="downloadCsv">
              <v-list-tile-title>CSV</v-list-tile-title>
            </v-list-tile>
            <v-list-tile @click="downloadExcel">
              <v-list-tile-title>Excel</v-list-tile-title>
            </v-list-tile>
          </v-list>
        </v-menu>
        <div class="action" v-if="isSystem" @click="workingsVisible = true" title="Reasoning and workings">
          <font-awesome-icon icon="cog" />
        </div>
        <div class="action" v-if="isSystem" @click="upvote"
          :class="{ 'selected': this.message.upVoted, 'actioned': upvoteActioned }" title="Response was good">
          <font-awesome-icon icon="thumbs-up" />
        </div>
        <div class="action" v-if="isSystem" @click="downvote"
          :class="{ 'selected': this.message.downVoted, 'actioned': downvoteActioned }" title="Response was bad">
          <font-awesome-icon icon="thumbs-down" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment"
import UserAvatar from '@/components/Users/UserAvatar'
import { mapState } from 'vuex'
import helpers from '@/helpers'
import ChatMessageText from "./message_types/ChatMessageText.vue"
import ChatMessageTable from "./message_types/ChatMessageTable.vue"
import ChatMessageChart from "./message_types/ChatMessageChart.vue"
import markdownit from 'markdown-it'
import { provider } from "@/provider.js"
import { getAllTableRows } from "@/services/chatTableStorage.js"
import { buildCsvBlob, buildExcelBlob, buildTsvText } from "@/stores/chat/exportTable.js"
export default {
  name: "ChatMessage",
  props: {
    message: {
      type: Object,
      required: true,
    },
  },
  components: {
    UserAvatar,
    ChatMessageText,
    ChatMessageTable,
    ChatMessageChart,
  },
  data() {
    return {
      md: markdownit(),
      showActions: false,
      actionOccurred: false,
      upvoteActioned: false,
      downvoteActioned: false,
      downloadActioned: false,
      downloadMenuOpen: false,
      copyActioned: false,
      debugVisible: false,
      workingsVisible: false,
      excludedStatuses: ["queued", "Analyzing your request", "Analyzing...", "Generating Result"],
      statusDots: "",
      statusDotsTimer: null,
      statusDotsCounter: 0,
      chartDisplaySubType: "bar",
    }
  },
  computed: {
    ...mapState({
      currentUser: state => state.currentUser.me
    }),
    chatChartOptionsEnabled() {
      const value = this.$route && this.$route.query && this.$route.query.chatChartOptions
      if (value == null || value === "") {
        return false
      }
      if (Array.isArray(value)) {
        return value.indexOf("1") > -1 || value.indexOf("true") > -1
      }
      const normalized = String(value).toLowerCase()
      return normalized === "1" || normalized === "true"
    },
    hasDownload() {
      const downloadTypes = ["table", "chart"]

      return downloadTypes.indexOf(this.message.display.type) > -1
    },
    isChartMessage() {
      return this.message.display && this.message.display.type === "chart"
    },
    chartSubTypeFromMessage() {
      const st = this.message.display && this.message.display.sub_type
      if (st === "pie" || st === "bar" || st === "line" || st === "mermaid") {
        return st
      }
      return "bar"
    },
    chartTypeSelectItems() {
      if (this.chartSubTypeFromMessage === "mermaid") {
        return [{ text: "Mermaid", value: "mermaid" }]
      }
      return [
        { text: "Bar", value: "bar" },
        { text: "Line", value: "line" },
        { text: "Pie", value: "pie" },
      ]
    },
    chartMessageExtraProps() {
      if (this.isChartMessage && this.chatChartOptionsEnabled) {
        return { displaySubType: this.chartDisplaySubType }
      }
      return {}
    },
    hasCopyClipboard() {
      return this.isSystem
    },
    isSystem() {
      return this.message.author.isBot
    },
    isUser() {
      return !this.message.author.isBot
    },
    isPending() {
      return !!this.message.__pending
    },
    formattedDate() {
      return moment(this.message.createdAt).calendar()
    },
    isAdmin() {
      return this.currentUser.isAccountAdmin
    },
    isLocalhost() {
      const h = typeof window !== "undefined" && window.location && window.location.hostname
      return h === "localhost" || h === "127.0.0.1"
    },
    historyAvailable() {
      return this.message.history
    },
    latest() {
      if (this.historyAvailable && Array.isArray(this.message.history) && this.message.history.length > 0) {
        return this.message.history.slice(-1)[0]
      }
      return { status: "Queued" }
    },
    inProgress() {
      return this.isSystem && this.message.text === "Processing..."
    },
    componentName() {
      // Guard: when display.type is missing/invalid, titleize returns "" and we'd get
      // "ChatMessage" - causing Vue to resolve to this component itself (infinite recursion).
      const type = this.message.display && this.message.display.type
      const validType = (type && ["text", "table", "chart"].includes(type)) ? type : "text"
      return `ChatMessage${helpers.titleize(validType)}`
    },
    reasoning() {
      return this.message.workings || ""
    },
    filteredStatusHistory() {
      const h = this.message.history
      if (!h || !Array.isArray(h)) return []
      return h.filter((s) => s && s.status && !this.excludedStatuses.includes(s.status))
    },
  },
  created() {
    this.syncChartDisplaySubTypeFromMessage()
  },
  mounted() {
    this.upvoteActioned = this.message.upVoted || this.message.downVoted
    this.downvoteActioned = this.message.upVoted || this.message.downVoted
    if (this.inProgress) this.startStatusDots()
  },
  beforeDestroy() {
    this.stopStatusDots()
  },
  watch: {
    "message.id"() {
      this.syncChartDisplaySubTypeFromMessage()
    },
    message() {
      this.upvoteActioned = this.message.upVoted || this.message.downVoted
      this.downvoteActioned = this.message.upVoted || this.message.downVoted
    },
    inProgress(isInProgress) {
      if (isInProgress) {
        this.startStatusDots()
      } else {
        this.stopStatusDots()
      }
    }
  },
  methods: {
    showDebug() {
      this.debugVisible = true
      this.$nextTick(() =>
        this.$refs.modal.focus()
      )
    },
    hideDebug() {
      this.debugVisible = false
    },
    hoverStart() {
      this.showActions = true
    },
    hoverEnd() {
      this.showActions = false
    },
    async downvote() {
      const updatedMessage = await provider.downVoteMessage(this.message.id)
      this.$store.dispatch("updateMessage", updatedMessage)
      this.actionOccurred = true
      this.downvoteActioned = true
      this.upvoteActioned = true
    },
    async upvote() {
      const updatedMessage = await provider.upVoteMessage(this.message.id)
      this.$store.dispatch("updateMessage", updatedMessage)
      this.actionOccurred = true
      this.downvoteActioned = true
      this.upvoteActioned = true
    },
    async getTableRows() {
      if (this.message.data && this.message.data.__indexedDb) {
        return getAllTableRows(this.message.data.key)
      }
      return Array.isArray(this.message.data) ? this.message.data : []
    },
    triggerFileDownload(blob, filename) {
      const link = document.createElement("a")
      const url = URL.createObjectURL(blob)
      link.setAttribute("href", url)
      link.setAttribute("download", filename)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    },
    exportFilename(extension) {
      return "caseblocks-export-" + moment().format("YYYY-MM-DD") + "." + extension
    },
    async downloadCsv() {
      const rows = await this.getTableRows()
      if (!rows || rows.length === 0) return
      this.triggerFileDownload(buildCsvBlob(rows), this.exportFilename("csv"))
      this.downloadActioned = true
      this.actionOccurred = true
    },
    async downloadExcel() {
      const rows = await this.getTableRows()
      if (!rows || rows.length === 0) return
      this.triggerFileDownload(buildExcelBlob(rows), this.exportFilename("xlsx"))
      this.downloadActioned = true
      this.actionOccurred = true
    },
    async download() {
      await this.downloadCsv()
    },
    async copyToClipboard(message) {
      if (message.display.type === "text") {
        navigator.clipboard.writeText(message.text)
      } else {
        const rows = await this.getTableRows()
        if (rows && rows.length > 0) {
          navigator.clipboard.writeText(buildTsvText(rows))
        }
      }
      this.copyActioned = true
      this.actionOccurred = true
    },
    syncChartDisplaySubTypeFromMessage() {
      if (this.isChartMessage) {
        this.chartDisplaySubType = this.chartSubTypeFromMessage
      }
    },
    async downloadChartImage() {
      const comp = this.$refs.messageTypeComponent
      if (!comp || typeof comp.downloadChartImage !== "function") {
        return
      }
      await comp.downloadChartImage()
      this.actionOccurred = true
    },
    title(text) {
      return helpers.titleize(text)
    },
    statusLabel(text) {
      const s = text && String(text).replace(/\.+$/, "") || ""
      return helpers.titleize(s)
    },
    startStatusDots() {
      this.stopStatusDots()
      this.statusDotsCounter = 0
      this.statusDots = ""
      const self = this
      this.statusDotsTimer = setInterval(function () {
        const dotsByStep = ["", ".", "..", "...", "...", "..."]
        self.statusDots = dotsByStep[self.statusDotsCounter] || ""
        self.statusDotsCounter = (self.statusDotsCounter + 1) % 6
      }, 500)
    },
    stopStatusDots() {
      if (this.statusDotsTimer) {
        clearInterval(this.statusDotsTimer)
        this.statusDotsTimer = null
      }
      this.statusDots = ""
      this.statusDotsCounter = 0
    },
    toolName(tool) {
      if (!tool) return "Unknown"
      const parts = String(tool).split("::")
      return parts[parts.length - 1] || tool
    },
    formatParams(params) {
      if (!params || typeof params !== "object") return ""
      try {
        return JSON.stringify(params, null, 2)
      } catch (e) {
        return String(params)
      }
    },
    formatEventDateSimple(d) {
      const datetime = new Date(d)
      return `${helpers.formatDate(datetime, "DD MMM YYYY")}, ${helpers.formatTime(datetime, "h:mma")}`
    }
  }
}
</script>

<style lang="scss">
:deep(.logo) {
  margin: 0;
  object-fit: fill;
}

.message-row {
  display: flex;
  justify-content: flex-start;

  &.chart {
    .message {
      max-width: initial;
      width: 100%;

      .main-section {
        width: 100%;

        .content {
          width: 100%;
        }
      }
    }
  }

  .message {
    min-width: 40%;
    max-width: 80%;
    margin-top: 20px;
    display: flex;
    flex-direction: column;


    /* Reasoning panel — scoped under .workings; tokens from designs/chat-reasoning.pen */
    .workings {
      --workings-ink: #1f2933;
      --workings-body: #3f4b5f;
      --workings-muted: #64748b;
      --workings-line: #e7ecf2;
      --workings-line-soft: #dee5ee;
      --workings-soft: #f3f5f8;
      --workings-pre-bg: #eaf0f6;
      --workings-code-bg: #eef1f5;
      --workings-code-ink: #4b5563;
      --workings-surface: #ffffff;

      margin-top: 16px;
      margin-left: 10px;
      padding: 0;
      width: 100%;
      border: 1px solid var(--workings-line);
      border-radius: 14px;
      background: var(--workings-soft);
      box-shadow: 0 18px 44px -18px rgba(31, 41, 51, 0.12);
      overflow: hidden;
      color: var(--workings-body);

      .workings-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 18px 22px 0;
        background: transparent;
        border-bottom: none;

        h3 {
          margin: 0;
          font-size: 14px;
          font-weight: 600;
          letter-spacing: 0;
          color: var(--workings-ink);
        }
      }

      .workings-close {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 30px;
        height: 30px;
        font-size: 14px;
        color: var(--workings-muted);
        padding: 0;
        line-height: 1;
        border-radius: 15px;
        background: var(--workings-surface);
        border: 1px solid var(--workings-line-soft);
        transition: color 0.15s ease, background 0.15s ease, border-color 0.15s ease;

        &:hover {
          color: var(--workings-ink);
          background: #f8fafc;
          border-color: var(--workings-line);
        }
      }

      .workings-body {
        padding: 18px 22px 24px;
        max-height: 60vh;
        overflow-y: auto;
      }

      .workings-reasoning {
        margin: 0;
        font-size: 13.5px;
        line-height: 1.48;
        color: var(--workings-body);

        > *:first-child {
          margin-top: 0;
        }

        > *:last-child {
          margin-bottom: 0;
        }

        h1,
        h2,
        h3,
        h4 {
          margin: 0 0 0.55em;
          line-height: 1.2;
          font-weight: 700;
          color: var(--workings-ink);
          letter-spacing: -0.01em;
        }

        h1,
        h2 {
          font-size: 21px;
        }

        h3,
        h4 {
          font-size: 14px;
          font-weight: 600;
        }

        p {
          margin: 0 0 0.9em;
          color: var(--workings-body);
          line-height: 1.48;
        }

        ul,
        ol {
          margin: 0 0 1em;
          padding: 0;
          list-style: none;
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        li {
          margin: 0;
          padding: 0;
          background: transparent;
          border: none;
          border-radius: 0;
          box-shadow: none;
          color: var(--workings-body);
          line-height: 1.45;

          strong {
            color: var(--workings-ink);
            font-weight: 600;
            font-size: 12.5px;
          }
        }

        /* Soft code chips — Pencil: #EEF1F5 / #4B5563 */
        code {
          font-family: "Geist Mono", ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace;
          font-size: 12px;
          font-weight: 400;
          color: var(--workings-code-ink);
          background: var(--workings-code-bg);
          border: none;
          border-radius: 7px;
          padding: 2px 8px;
          word-break: break-word;
        }

        /* Soft inset pre — Pencil outer #EAF0F6, inner white */
        pre {
          margin: 0 0 1em;
          padding: 14px;
          overflow-x: auto;
          background: var(--workings-pre-bg);
          border: 1px solid var(--workings-line-soft);
          border-radius: 12px;
          box-shadow: none;

          code {
            display: block;
            padding: 12px;
            background: var(--workings-surface);
            border: 1px solid var(--workings-line);
            border-radius: 9px;
            color: var(--workings-code-ink);
            font-size: 12px;
            font-weight: 400;
            line-height: 1.45;
            white-space: pre-wrap;
            word-break: break-word;
          }
        }

        a {
          color: #2f6fed;
          text-decoration: none;

          &:hover {
            text-decoration: underline;
          }
        }

        em {
          color: var(--workings-muted);
          font-style: italic;
        }

        blockquote {
          margin: 0 0 1em;
          padding: 10px 12px;
          border-left: 3px solid var(--workings-line-soft);
          background: rgba(255, 255, 255, 0.7);
          color: var(--workings-muted);
          border-radius: 0 10px 10px 0;
        }
      }

      .workings-empty {
        color: var(--workings-muted);
        font-style: italic;
        margin: 0;
      }

      .workings-debug-actions {
        margin-top: 14px;
      }

      .workings-debug-btn {
        font-size: 0.88em;
        padding: 6px 12px;
        color: var(--workings-ink);
        background: var(--workings-surface);
        border: 1px solid var(--workings-line);
        border-radius: 8px;
        cursor: pointer;

        &:hover {
          background: var(--workings-code-bg);
        }
      }

      h4 {
        margin-top: 10px;
        margin-bottom: 4px;
        padding-bottom: 4px;
        color: var(--workings-ink);
      }

      hr {
        margin: 14px 0;
        border: none;
        border-top: 1px solid var(--workings-line);
      }

      .tool-params {
        font-size: 12px;
        max-height: 200px;
        overflow: auto;
        padding: 12px;
        color: var(--workings-code-ink);
        background: var(--workings-surface);
        border: 1px solid var(--workings-line);
        border-radius: 9px;
        line-height: 1.45;
      }
    }

    &.show-actions {
      .action-container {
        opacity: 1;
      }
    }

    .header {
      display: flex;
      justify-content: space-between;
      font-size: 0.8em;

      .author {
        font-weight: 600;

        ::v-deep .v-chip {
          margin-bottom: 0 !important;
          margin-top: 0 !important;
        }
      }
    }

    .content {
      padding: 10px;

      :last-child {
        margin-bottom: 0;
      }


      &.is-text {
        p {
          margin: 5px 0;
        }

        h2 {
          margin-top: 20px;
          margin-bottom: 10px;
        }

        h3 {
          margin-top: 20px;
          margin-bottom: 10px;
        }

        h4 {
          margin-top: 20px;
          margin-bottom: 10px;
        }

        h5 {
          margin-top: 20px;
          margin-bottom: 10px;
        }

        h6 {
          margin-top: 20px;
          margin-bottom: 10px;
        }

        blockquote {
          margin-top: 20px;
          margin-bottom: 10px;
        }

        code {
          margin-top: 20px;
          margin-bottom: 10px;
        }

        pre {
          margin-top: 10px;
          margin-bottom: 20px;
        }

        hr {
          margin-top: 10px;
          margin-bottom: 20px;
        }

        table {
          margin-top: 10px;
          margin-bottom: 20px;
        }

        ul,
        ol {
          margin-left: 20px;
          margin-top: 10px;
          margin-bottom: 10px;
        }
      }

      .modal {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        display: flex;
        justify-content: center;
        align-items: center;

        .modal-background {
          position: absolute;
          opacity: .8;
          background: black;
          width: 100%;
          height: 100%;
          z-index: 2000;

        }

        .modal-content {
          position: relative;
          height: 80%;
          width: 80%;
          max-width: 800px;
          background: white;
          z-index: 2001;
          overflow: auto;
          padding: 16px;
          border-radius: 8px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);

          .modal-actions {
            position: absolute;
            top: 12px;
            right: 12px;
          }

          .modal-close {
            font-size: 1.2em;
            padding: 6px;
            background: transparent;
            border: none;
            cursor: pointer;
            color: #666;

            &:hover {
              color: #000;
            }
          }

          h2 {
            margin-top: 16px;
            margin-bottom: 8px;
            font-size: 1.1em;
          }

          pre {
            margin: 8px 0 16px;
            padding: 12px;
            background: #f5f5f5;
            border-radius: 4px;
            overflow: auto;
            font-size: 0.85em;
          }
        }
      }

    }

    .main-section {
      // background: #ccc;
      padding: 8px;
      border-radius: 40px;

    }

    .action-container {
      display: flex;
      justify-content: flex-end;
      align-items: center;
      flex-wrap: wrap;
      margin: 6px 13px;
      opacity: 0;
      transition: opacity 0.2s ease-in-out;
      font-size: 1.1em;


      color: #666;

      .action-chart-type-select {
        max-width: 160px;
        flex: 0 1 160px;
        margin-left: 10px;
        font-size: 14px;
      }

      .v-menu {
        display: inline-flex;
        margin-left: 14px;

        .action {
          margin-left: 0;
        }
      }

      .action {
        margin-left: 14px;
        cursor: pointer;

        &.actioned {
          color: #ccc;

          &.selected {
            color: #000;
          }

        }



        &:hover {
          opacity: 0.7;
        }
      }
    }
  }

  &.system {
    .message {
      max-width: 100%;

      .header {
        margin-right: 7px;
        display: none;

        .author {
          img {
            width: 32px;
            height: 32px;
            border: 1px solid #999;
            border-radius: 16px;
            padding: 5px;
            box-shadow: 0px 0px 10px 1px rgb(160 160 160 / 75%);
          }
        }
      }

      .content {
        padding-top: 0;
      }

    }
  }

  &.user {
    justify-content: flex-end;

    .message {
      .main-section {
        background: var(--user-message-background);
        color: var(--user-message-color);
      }
    }

    &.pending .message .main-section {
      opacity: 0.75;
    }
  }

  .status {
    color: #aaa;
  }
}
</style>
